"use client";

import { Button, buttonVariants } from "@/components/atoms/buttons";
import { PriceToastDropdown } from "@/components/atoms/price-toast-dropdown";
import { Toast, toast } from "@/components/atoms/toast";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/hooks/useCart";
import { useAddToCart } from "@/hooks/useCartMutations";
import { Locales } from "@/lib/i18n";
import { __, _x, getPermalink } from "@/lib/i18n/functions";
import { cn, formatPrice } from "@/lib/utils";
import { Loader2, Minus, Plus, ShoppingCart } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useProductQuantity } from "@/hooks/useProductQuantity";
import { getCartQuantityStep, normalizeConditioning, toLotQuantity, toPiecePrice } from "@/lib/product-units";

type InspirationProductCardProps = {
  image: string;
  altText: string;
  productName: string;
  link: string;
  price: string;
  locale: Locales;
  isLoading: boolean;
  medusaVariantId: string;
  onQuantityChange?: (quantity: number) => void;
  productConditioning: number | string;
  productPrice: number;
  isFullImage: boolean;
  isOutOfStock?: boolean;
  isPriceDynamic?: boolean;
  isRange?: boolean;
  forSale?: boolean;
};

const InspirationProductCard = ({
  image,
  altText,
  productName,
  link,
  price,
  locale,
  medusaVariantId,
  isLoading,
  onQuantityChange,
  productConditioning,
  productPrice,
  isFullImage,
  isOutOfStock = false,
  isPriceDynamic = false,
  isRange = false,
  forSale = false,
}: InspirationProductCardProps) => {
  const quantityStep = getCartQuantityStep(productConditioning);
  const physicalPieceCount = normalizeConditioning(productConditioning);
  const [count, setCount] = useProductQuantity(productConditioning);
  const { cart } = useCart();
  const cartItem = cart?.items && cart?.items.find((item) => item.variant_id === medusaVariantId);
  const isInCart = Boolean(cartItem);
  const cartQuantity = toLotQuantity(cartItem?.quantity || 0, productConditioning).toString();

  const { addToCart, isLoading: isAddingToCart } = useAddToCart({
    onSuccess: () => {
      toast.success({ title: __("Item added to cart!", locale) });
    },
    onError: (error) => {
      console.error("Failed to add to cart:", error);
      toast.error({ title: __("Failed to add item to cart", locale) });
    },
  });

  const incrementCount = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCount(count + quantityStep);
    onQuantityChange?.(count + quantityStep);
  };

  const decrementCount = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (count > quantityStep) {
      setCount(count - quantityStep);
      onQuantityChange?.(count - quantityStep);
    }
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(medusaVariantId, count);
  };

  const pricePerPiece = toPiecePrice(productPrice, productConditioning);

  return (
    <article className="hover:shadow-festi-100 group relative h-fit w-full min-w-[448px] flex-1 rounded-lg border border-transparent p-2.5 transition-all duration-300 ease-in-out hover:border-black/5">
      <Link
        href={link}
        className="focus-visible:shadow-festi absolute inset-0 z-10 rounded-md focus:outline-none focus-visible:border focus-visible:border-black/5"
        aria-label={_x("View the product {{ productName }}", { productName }, locale)}
      >
        <span className="sr-only">{_x("View the product {{ productName }}", { productName }, locale)}</span>
      </Link>
      <div className="flex gap-5">
        <figure className="bg-product-card relative flex size-[132px] shrink-0 items-center justify-center overflow-hidden rounded-md">
          {!isLoading &&
            (isFullImage ? (
              <Image src={image} alt={altText} fill className="absolute shrink-0 object-cover mix-blend-multiply" />
            ) : (
              <div className="relative size-[94px]">
                <Image src={image} alt={altText} fill className="absolute shrink-0 object-contain mix-blend-multiply" />
              </div>
            ))}
        </figure>
        <div className={cn("flex w-full flex-col gap-2.5", isOutOfStock && "gap-0")}>
          <div className="flex items-center justify-between gap-2">
            <h5 className="leading-body-md text-lg">{productName}</h5>
            {forSale && <Badge variant="festi">{__("To sell", locale)}</Badge>}
          </div>
          {isRange || isPriceDynamic ? (
            <PriceToastDropdown locale={locale} showOnlyIcon={false} />
          ) : (
            <dl className="flex w-full items-center justify-between">
              <div>
                <dt className="sr-only">{__("Product Price", locale)}</dt>
                <dd className="font-cabinet-grotesk font-medium text-gray-900">
                  {isLoading ? (
                    <span className="shimmer inline-block h-8 w-18 rounded" />
                  ) : (
                    <>
                      {price}{" "}
                      {physicalPieceCount > 1 && (
                        <mark className="text-primary-light bg-transparent text-sm font-normal">
                          /{physicalPieceCount} {__("piece", locale)}
                        </mark>
                      )}
                    </>
                  )}
                </dd>
              </div>
              <div>
                <dt className="sr-only">{__("Product per unit", locale)}</dt>
                <dd className="text-primary-light text-sm">
                  {isLoading ? (
                    <span className="shimmer inline-block h-6 w-12 rounded" />
                  ) : (
                    <>
                      {physicalPieceCount > 1 && (
                        <span>{`${formatPrice(pricePerPiece, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })} ${__("/unit", locale)}`}</span>
                      )}
                    </>
                  )}
                </dd>
              </div>
            </dl>
          )}
          {!isLoading && (
            <div className="relative z-20 mt-auto flex w-full items-center gap-1.25">
              {isOutOfStock ? (
                <Toast
                  id={`inspiration-${medusaVariantId}`}
                  type="info"
                  className="min-[384px]:min-w-none w-full max-w-none shadow-none max-sm:p-2"
                  titleClassName="max-sm:text-[10px]"
                  title={__("This product is out of stock", locale)}
                />
              ) : isInCart ? (
                // Show "In Cart" state with quantity and go to cart button
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-1 text-[10px] md:text-sm">
                    <ShoppingCart size={12} className="text-green-600 md:size-4" aria-hidden="true" />
                    <span className="font-medium text-green-600">
                      {_x("In cart ({{ cartQuantity }})", { cartQuantity }, locale)}
                    </span>
                  </div>
                  <Link
                    href={getPermalink(locale, "cart")}
                    aria-label={__("Go to cart", locale)}
                    className={cn(
                      buttonVariants(),
                      "flex h-6.5 items-center justify-center rounded-xs p-0 px-2 text-[10px] transition-transform duration-150 ease-in-out active:scale-95 md:h-10 md:px-4 md:text-sm"
                    )}
                  >
                    {__("View Cart", locale)}
                  </Link>
                </div>
              ) : (
                <>
                  <span className="hidden text-xs md:block md:text-sm">{__("QUANTITY", locale)}</span>
                  <div className="bg-product-card flex h-[26px] w-full items-center justify-between rounded-sm sm:h-10">
                    <button
                      onClick={decrementCount}
                      className="group flex h-full w-6 cursor-pointer items-center justify-center md:w-8"
                      aria-label={__("Decrease quantity", locale)}
                    >
                      <Minus
                        size={12}
                        className={cn(
                          count <= quantityStep ? "text-primary-light/50" : "text-primary",
                          "group-focus:text-dark-primary"
                        )}
                        aria-hidden="true"
                      />
                    </button>
                    <span
                      className="text-xs font-medium tabular-nums md:text-base"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      {toLotQuantity(count, productConditioning)}
                    </span>
                    <button
                      onClick={incrementCount}
                      className="group flex h-full w-6 cursor-pointer items-center justify-center md:w-8"
                      aria-label={__("Increase quantity", locale)}
                    >
                      <Plus size={12} className="text-primary group-focus:text-dark-primary" aria-hidden="true" />
                    </button>
                  </div>
                  <Button
                    disabled={isAddingToCart}
                    onClick={handleAddToCart}
                    aria-label={_x("Add {{ productName }} to cart", { productName }, locale)}
                    className="h-8 w-16 rounded-xs text-[10px] transition-transform duration-150 ease-in-out active:scale-95 md:h-10 md:w-[4.625rem] md:text-sm"
                  >
                    {isAddingToCart ? <Loader2 aria-hidden="true" className="animate-spin" /> : __("Add", locale)}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default InspirationProductCard;
